import { ReactNode } from 'react'
import { IconButton, IconButtonProps, Stack, StackProps, Text } from '~/design_system'
import { ArrowLeft, ArrowRight } from '~/icons'

export type PagerProps = StackProps

/**
 * @deprecated This component has been replaced by our new Pager component as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 *
 * The container for a pager. This sets up the layout for the buttons and text.
 */
export const Pager = ({ children, ...props }: PagerProps) => (
  <Stack align="center" isInline justify="center" spacing={[2, 6]} shouldWrapChildren {...props}>
    {children}
  </Stack>
)

/**
 * @deprecated This component is not longer available as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 *
 *`BasePagerButtonProps` is a combination of custom props (`data-testid` and
 * `isFlipped`) along with required (`aria-label`, `icon`, and `onClick`) and
 * optional (`isDisabled`) props from `IconButtonProps`. We use the `Required`
 * type to ensure that these props are passed to the component.
 *
 * As a component in the component library, we only want to allow a specific
 * range of customization of the buttons to ensure that these components look
 * consistent across our pages. That's why we constrain the props to a small
 * subset of props from `IconButtonProps`, rather than allowing any of its
 * props to be passed.
 */
export type BasePagerButtonProps = {
  /** The test id of the button. */
  'data-testid'?: string
} & Required<Pick<IconButtonProps, 'aria-label' | 'icon' | 'onClick'>> &
  Pick<IconButtonProps, 'isDisabled'>

/**
 * The base button used to render the previous and next buttons.
 */
function BasePagerButton({ ref, ...props }: BasePagerButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  return <IconButton ref={ref} variant="secondary" {...props} />
}
BasePagerButton.displayName = 'BasePagerButton'

// Props that a consumer MUST pass to each button to ensure that it works
// correctly. In this case, that's just `onClick`.
type RequiredButtonProps = Required<Pick<BasePagerButtonProps, 'onClick'>>

// Props that a consumer CAN pass to each button in order to customize the
// behavior and a11y of the component.
type OptionalButtonProps = Partial<Pick<BasePagerButtonProps, 'aria-label' | 'isDisabled' | 'data-testid'>>

export type PagerButtonProps = RequiredButtonProps & OptionalButtonProps

/**
 * @deprecated This component has been replaced by our new PagerPreviousButton component as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 *
 * The previous button for a pager.
 */
export function PagerPreviousButton({
  ref,
  'aria-label': ariaLabel = 'Previous page',
  'data-testid': dataTestId = 'previous-page',
  ...props
}: PagerButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  return <BasePagerButton ref={ref} icon={<ArrowLeft />} aria-label={ariaLabel} data-testid={dataTestId} {...props} />
}
PagerPreviousButton.displayName = 'PagerPreviousButton'

/**
 * @deprecated This component has been replaced by our new PagerNextButton component as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 *
 * The next button for a pager.
 */
export function PagerNextButton({
  ref,
  'aria-label': ariaLabel = 'Next page',
  'data-testid': dataTestId = 'next-page',
  ...props
}: PagerButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  return <BasePagerButton ref={ref} icon={<ArrowRight />} aria-label={ariaLabel} data-testid={dataTestId} {...props} />
}
PagerNextButton.displayName = 'PagerNextButton'

export type PagerTextProps = {
  children: ReactNode
  'data-testid'?: string
}

/**
 * @deprecated This component has been replaced by our new PagerTotalItems component as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 *
 * The content rendered between the pager buttons.
 */
export const PagerText = ({ children, 'data-testid': testId }: PagerTextProps) => (
  <Text data-testid={testId} m={0}>
    {children}
  </Text>
)
